import VueI18n from 'vue-i18n';

export const sensorHasCapability = (sensor, capability) => {
  const sensorDefinition = SENSOR_DEFINITIONS[sensor.type] || { capabilities: {} };
  return sensorDefinition.capabilities[capability] || false;
};

const DATA_TYPES = {
  IMAGES: 'images',
  VIDEOS: 'videos',
  IMAGE_AND_VIDEOS: 'images_and_videos',
  // 画像も動画も扱わないセンサー向け。capabilities.hasMedia が false のとき、
  // 扱うメディアの種類も存在しないのでこの値を選ぶ。
  NONE: 'none',
};

export const SENSOR_TYPES = {
  FLY: 'fly',
  FLY_COUNT: 'fly_count',
  FLY_SCAN: 'fly_scan',
  INSECT_V2: 'insect_monitor_v2',
  RAT: 'rat_monitor',
  RAT_SENSE: 'rat_sense',
  HYKECAM: 'hykecam',
  STATION: 'station',
  MIRAKUN_DEVICE: 'mirakun_device',
};

export const SENSOR_DEFINITIONS = {
  hykecam: {
    dataType: DATA_TYPES.IMAGE_AND_VIDEOS,
    capabilities: {
      upload: {
        accept: ['image/jpeg', 'image/png', 'video/quicktime', '.mov'],
      },
      health_check: false,
      threshold: false,
      receives_mails: true,
      replacement: false,
      photoInterval: false,
      viewDetectionDetails: false,
      csvExport: false,
      hasMedia: true,
      sendsNotifications: true,
      identifierLabelKey: 'field.identifier',
      identifierFormat: 'alphanumeric_underscore',
      hasPlace: true,
      canRelocate: true,
      shareable: true,
    },
  },
  insect_monitor_v2: {
    discontinued: true,
    dataType: DATA_TYPES.IMAGES,
    capabilities: {
      upload: false,
      health_check: true,
      threshold: true,
      receives_mails: false,
      replacement: false,
      photoInterval: false,
      viewDetectionDetails: true,
      csvExport: true,
      hasMedia: true,
      sendsNotifications: true,
      identifierLabelKey: 'field.identifier',
      identifierFormat: 'alphanumeric_underscore',
      hasPlace: true,
      canRelocate: true,
      shareable: true,
    },
  },
  rat_monitor: {
    dataType: DATA_TYPES.VIDEOS,
    capabilities: {
      upload: false,
      health_check: false,
      threshold: false,
      receives_mails: false,
      replacement: false,
      photoInterval: false,
      viewDetectionDetails: false,
      csvExport: true,
      hasMedia: true,
      sendsNotifications: true,
      identifierLabelKey: 'field.identifier',
      identifierFormat: 'alphanumeric_underscore',
      hasPlace: true,
      canRelocate: true,
      shareable: true,
    },
  },
  rat_sense: {
    dataType: DATA_TYPES.NONE,
    capabilities: {
      upload: false,
      health_check: false,
      threshold: false,
      receives_mails: false,
      replacement: false,
      photoInterval: false,
      viewDetectionDetails: false,
      csvExport: false,
      // RatSense は赤外線センサーで画像も動画も持たず、Pest-Vision にもデータを蓄積しない。
      // メディアの実体を前提にした表示（動画一覧・サムネイル・押せる見た目）を一律で出さない。
      hasMedia: false,
      // Pest-Vision に event を蓄積しないため通知メール送信の起点も持たない。
      sendsNotifications: false,
      identifierLabelKey: 'field.identifier',
      // mp_id は半角数字のみで、英字・アンダースコアは受理しない。
      // ヒント文言と入力チェックの両方をこの値から一意に決める。
      identifierFormat: 'digits_only',
      // RatSense のモニタリングポイントは Cre8tec 側で位置管理されるため、
      // Pest-Vision 側で「設置場所」を持つ意味がない。フィールド自体を非表示化。
      hasPlace: false,
      canRelocate: true,
      // 共有はセンサーの閲覧権限を他ユーザーへ渡す機能。RatSense は Cre8tec 側の
      // mp_id を参照するだけで Pest-Vision 上に閲覧対象のデータを持たないため、
      // 権限を渡す相手に見せるものが無い。共有セクションごと非表示化する。
      shareable: false,
      // 通信強度は Cre8tec 側の通信状態から取り込む値で、RatSense だけが持つ。
      // 他機種は SENSOR_DEFINITIONS にキーを置かず sensorHasCapability の
      // フォールバックで false になる。
      hasSignalStrength: true,
      // 検討: RatSense 固有 capability が増え続けるようなら、
      // 専用フォーム component (RatSenseForm.vue) 分割を再検討する。
    },
  },
  fly_count: {
    dataType: DATA_TYPES.IMAGES,
    capabilities: {
      upload: false,
      health_check: true,
      threshold: true,
      receives_mails: false,
      replacement: true,
      photoInterval: true,
      viewDetectionDetails: false,
      csvExport: true,
      hasMedia: true,
      sendsNotifications: true,
      identifierLabelKey: 'field.identifier',
      identifierFormat: 'alphanumeric_underscore',
      hasPlace: true,
      canRelocate: true,
      shareable: true,
    },
  },
  fly_scan: {
    dataType: DATA_TYPES.IMAGES,
    capabilities: {
      upload: false,
      health_check: true,
      threshold: true,
      receives_mails: false,
      replacement: true,
      photoInterval: true,
      viewDetectionDetails: true,
      csvExport: true,
      hasMedia: true,
      sendsNotifications: true,
      identifierLabelKey: 'field.identifier',
      identifierFormat: 'alphanumeric_underscore',
      hasPlace: true,
      canRelocate: true,
      shareable: true,
    },
  },
  fly: {
    discontinued: true,
    dataType: DATA_TYPES.IMAGES,
    capabilities: {
      upload: false,
      health_check: true,
      threshold: true,
      receives_mails: false,
      replacement: true,
      photoInterval: true,
      viewDetectionDetails: true,
      csvExport: true,
      hasMedia: true,
      sendsNotifications: true,
      identifierLabelKey: 'field.identifier',
      identifierFormat: 'alphanumeric_underscore',
      hasPlace: true,
      canRelocate: true,
      shareable: true,
    },
  },
};

// eslint-disable-next-line
const i18n = new VueI18n({
  locale: 'en',
  messages: {
    en: {
      fly: 'F-type',
      fly_count: 'FC-type',
      fly_scan: 'FS-type',
      insect_monitor_v2: 'Insect sensors(3 camera)',
      rat_monitor: 'R-type',
      rat_sense: 'RatSense',
      hykecam: 'Hyke Cam',
      station: 'S-type',
      mirakun_device: 'Chemical treatment system',
    },
    ja: {
      fly: 'F型',
      fly_count: 'FC型',
      fly_scan: 'FS型',
      insect_monitor_v2: '捕虫器センサー(3台)',
      rat_monitor: 'R型',
      rat_sense: 'RatSense',
      hykecam: 'ハイクカム',
      station: 'S型',
      mirakun_device: '自動投薬機',
    },
  },
});

export default {
  computed: {
    SENSOR_DEFINITIONS() {
      return SENSOR_DEFINITIONS;
    },
    SENSOR_TYPES() {
      return SENSOR_TYPES;
    },
    SENSOR_TYPE_NAMES() {
      const entries = Object.values(SENSOR_TYPES).map((value) =>
        ([value, i18n.t(value, this.$i18n.locale)]));
      return Object.fromEntries(entries);
    },
    SENSOR_TYPE_INDEXES() {
      const entries = Object.values(SENSOR_TYPES).map((value, index) => ([value, index]));
      return Object.fromEntries(entries);
    },
  },
};
